import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton
} from '@mui/material';
import {
  ExpandMore,
  ChevronRight,
  Folder,
  FolderOpen
} from '@mui/icons-material';
import { useApiClient } from '../services/apiClient';

const FolderTreeItem = ({ folder, level = 0, currentPath, onPathChange, onLoadSubfolders }) => {
  const [expanded, setExpanded] = useState(false);
  const [subfolders, setSubfolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const isSelected = currentPath === folder.path;
  const hasSubfolders = folder.hasSubfolders || subfolders.length > 0;

  const handleToggle = async () => {
    if (!expanded && !loaded) {
      setLoading(true);
      try {
        const folders = await onLoadSubfolders(folder.path);
        setSubfolders(folders);
        setLoaded(true);
      } catch (error) {
        console.error('Error loading subfolders:', error);
      } finally {
        setLoading(false);
      }
    }
    setExpanded(!expanded);
  };

  const handleSelect = () => {
    onPathChange(folder.path);
  };

  return (
    <>
      <ListItem 
        disablePadding 
        sx={{ 
          pl: level * 2,
          backgroundColor: isSelected ? 'action.selected' : 'transparent',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {hasSubfolders && (
            <IconButton 
              size="small" 
              onClick={handleToggle}
              sx={{ mr: 0.5 }}
            >
              {loading ? (
                <CircularProgress size={16} />
              ) : expanded ? (
                <ExpandMore />
              ) : (
                <ChevronRight />
              )}
            </IconButton>
          )}
          
          <ListItemButton 
            onClick={handleSelect}
            sx={{ 
              flex: 1,
              py: 0.5,
              minHeight: 'auto',
              pl: hasSubfolders ? 0 : 3
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {expanded ? (
                <FolderOpen sx={{ fontSize: '1.2rem' }} color="primary" />
              ) : (
                <Folder sx={{ fontSize: '1.2rem' }} color="action" />
              )}
            </ListItemIcon>
            
            <ListItemText 
              primary={folder.name}
              primaryTypographyProps={{
                variant: 'body2',
                sx: { 
                  fontWeight: isSelected ? 'bold' : 'normal',
                  color: isSelected ? 'primary.main' : 'inherit'
                }
              }}
            />
          </ListItemButton>
        </Box>
      </ListItem>
      
      {hasSubfolders && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {subfolders.map((subfolder, index) => (
              <FolderTreeItem
                key={subfolder.path}
                folder={subfolder}
                level={level + 1}
                currentPath={currentPath}
                onPathChange={onPathChange}
                onLoadSubfolders={onLoadSubfolders}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const FolderTree = ({ currentPath, onPathChange }) => {
  const apiClient = useApiClient();
  const [rootFolders, setRootFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 특정 경로의 하위 폴더들을 로드
  const loadSubfolders = async (path = '') => {
    try {
      console.log('Loading subfolders for path:', path);
      const result = await apiClient.listObjects(path);
      
      // 폴더만 필터링하고 hasSubfolders 속성 추가
      const folders = result.items?.filter(item => item.type === 'folder').map(folder => ({
        ...folder,
        hasSubfolders: true // 일단 모든 폴더가 하위 폴더를 가질 수 있다고 가정
      })) || [];
      
      return folders;
      
    } catch (err) {
      console.error('Error loading subfolders:', err);
      throw err;
    }
  };

  // 초기 루트 폴더들 로드
  useEffect(() => {
    const initializeTree = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const folders = await loadSubfolders('');
        setRootFolders(folders);
      } catch (err) {
        setError(`폴더를 불러오는데 실패했습니다: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    initializeTree();
  }, []);

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <Folder sx={{ mr: 1 }} />
          폴더 구조
        </Typography>
      </Box>
      
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      
      <List component="nav" disablePadding>
        {/* 루트 폴더 */}
        <ListItem 
          disablePadding
          sx={{ 
            backgroundColor: currentPath === '' ? 'action.selected' : 'transparent',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          <ListItemButton onClick={() => onPathChange('')}>
            <ListItemIcon>
              <FolderOpen color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Root"
              primaryTypographyProps={{
                variant: 'body2',
                sx: { 
                  fontWeight: currentPath === '' ? 'bold' : 'normal',
                  color: currentPath === '' ? 'primary.main' : 'inherit'
                }
              }}
            />
          </ListItemButton>
        </ListItem>
        
        {/* 하위 폴더들 */}
        {rootFolders.map((folder, index) => (
          <FolderTreeItem
            key={folder.path}
            folder={folder}
            level={1}
            currentPath={currentPath}
            onPathChange={onPathChange}
            onLoadSubfolders={loadSubfolders}
          />
        ))}
      </List>
    </Box>
  );
};

export default FolderTree;
